import { useCallback, useState } from 'react';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { UNAUTHENTICATED_FEEDBACK_FORM_URL } from './constants';

type SendFeedback = {
	/** Whether the in-product feedback form should be rendered. */
	isFeedbackOpen: boolean;
	/**
	 * Opens the feedback form, or sends a signed-out visitor to the standalone
	 * form in a new tab, which is the only route they have.
	 */
	openFeedback: () => void;
	closeFeedback: () => void;
};

/**
 * The bar's "send feedback" behaviour, shared by the two surfaces that offer
 * it: the inline button in the right-hand cluster (`FeedbackControl`) and the
 * item in the collapsed hamburger (`NavMenu`). Only one of those is reachable
 * at any viewport width — the button hides at 30rem, which is exactly where the
 * menu item appears — so each renders its own `CollectFeedbackV2` rather than
 * coordinating one between them.
 */
export const useSendFeedback = (): SendFeedback => {
	const { isAuthed } = useAtlassianUserContext();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	const openFeedback = useCallback((): void => {
		if (!isAuthed) {
			globalThis.window.open(UNAUTHENTICATED_FEEDBACK_FORM_URL, '_blank');
			return;
		}

		setIsFeedbackOpen(true);
	}, [isAuthed]);

	const closeFeedback = useCallback((): void => {
		setIsFeedbackOpen(false);
	}, []);

	return { isFeedbackOpen, openFeedback, closeFeedback };
};
